import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
    Truck,
    Package,
    ArrowRight,
    CheckCircle2,
    Sparkles,
    IndianRupee,
    Calendar,
    RotateCcw,
} from "lucide-react";
import { services } from "@/data/content";
import useSeo from "@/hooks/useSeo";

// Crude but believable pricing heuristic (frontend-only mock)
const SERVICE_RATES = {
    "3pl-logistics": { base: 2400, perKg: 14, perKm: 32, days: 2 },
    warehousing: { base: 1800, perKg: 8, perKm: 0, days: 1 },
    transporting: { base: 1500, perKg: 11, perKm: 28, days: 3 },
    "auto-industry": { base: 3200, perKg: 18, perKm: 36, days: 2 },
    "cold-chain": { base: 2800, perKg: 16, perKm: 40, days: 2 },
};

function calculateQuote({ service, weight, distance, urgency }) {
    const rate = SERVICE_RATES[service] || SERVICE_RATES["transporting"];
    const base = rate.base + rate.perKg * Number(weight) + rate.perKm * Number(distance);
    const urgencyMul = urgency === "express" ? 1.45 : urgency === "priority" ? 1.18 : 1;
    const total = Math.round(base * urgencyMul);
    const tax = Math.round(total * 0.18);
    const days = urgency === "express" ? Math.max(1, rate.days - 1) : urgency === "priority" ? rate.days : rate.days + 1;
    return {
        subtotal: total,
        gst: tax,
        total: total + tax,
        etaDays: days,
    };
}

const urgencies = [
    { key: "standard", label: "Standard", desc: "Best value" },
    { key: "priority", label: "Priority", desc: "+18% · faster lanes" },
    { key: "express", label: "Express", desc: "+45% · dedicated" },
];

export default function QuoteRequest() {
    useSeo({
        title: "Instant Quote — Price Your Shipment in 60 Seconds",
        description:
            "Get an instant logistics quote with ETA and GST. Pick a service, origin, destination, weight and urgency — see a live estimate from Beeta Logistics.",
        path: "/quote",
    });
    const [searchParams] = useSearchParams();
    const requestedService = searchParams.get("service");
    const initialService = services.some((s) => s.slug === requestedService)
        ? requestedService
        : "transporting";
    const [form, setForm] = useState({
        origin: "",
        destination: "",
        weight: "",
        distance: "",
        service: initialService,
        urgency: "standard",
        name: "",
        email: "",
    });
    const [quote, setQuote] = useState(null);
    const [error, setError] = useState("");

    const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const handleSubmit = (e) => {
        e.preventDefault();
        setError("");
        if (!form.origin || !form.destination || !form.weight || !form.distance) {
            setError("Please fill origin, destination, weight and distance.");
            return;
        }
        if (Number(form.weight) <= 0 || Number(form.distance) <= 0) {
            setError("Weight and distance must be positive numbers.");
            return;
        }
        setQuote(calculateQuote(form));
        setTimeout(() => {
            document.getElementById("quote-result")?.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 100);
    };

    const reset = () => {
        setQuote(null);
        setError("");
    };

    return (
        <div data-testid="quote-page" className="bg-white">
            {/* HEADER */}
            <section className="relative pt-16 pb-20 bg-slate-50 border-b border-slate-200 overflow-hidden">
                <div className="absolute inset-0 bg-grid-slate opacity-40" />
                <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-50 border border-blue-100 mb-6">
                        <Sparkles className="w-3.5 h-3.5 text-blue-600" />
                        <span className="text-xs font-bold uppercase tracking-[0.18em] text-blue-700">
                            Instant Quote · 60 seconds
                        </span>
                    </div>
                    <h1 className="font-display text-4xl sm:text-6xl font-black text-slate-900 tracking-tighter leading-[1.08] sm:leading-[0.95]">
                        Price your shipment.
                        <br />
                        <span className="text-blue-600">No calls required.</span>
                    </h1>
                    <p className="mt-6 text-lg text-slate-600 max-w-2xl">
                        Tell us what needs to move. We&apos;ll show a live estimate with GST and
                        ETA — instantly.
                    </p>
                </div>
            </section>

            {/* FORM */}
            <section className="py-16">
                <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
                    <form
                        onSubmit={handleSubmit}
                        data-testid="quote-form"
                        className="bg-white rounded-3xl border border-slate-200 shadow-sm p-6 md:p-10 space-y-8"
                    >
                        {/* Service type */}
                        <div>
                            <label className="block text-sm font-bold text-slate-900 mb-4">
                                Service type
                            </label>
                            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
                                {services.map((s) => {
                                    const Icon = s.icon;
                                    const active = form.service === s.slug;
                                    return (
                                        <button
                                            type="button"
                                            key={s.slug}
                                            data-testid={`service-pick-${s.slug}`}
                                            onClick={() => update("service", s.slug)}
                                            className={`group text-left p-5 rounded-2xl border transition-all ${
                                                active
                                                    ? "border-slate-900 bg-slate-900 text-white shadow-xl"
                                                    : "border-slate-200 bg-white hover:border-slate-400"
                                            }`}
                                        >
                                            <Icon className={`w-5 h-5 mb-3 ${active ? "text-blue-300" : "text-blue-600"}`} />
                                            <div className={`font-display font-bold text-base ${active ? "text-white" : "text-slate-900"}`}>
                                                {s.title}
                                            </div>
                                            <div className={`text-xs mt-1 ${active ? "text-slate-300" : "text-slate-500"}`}>
                                                {s.tagline}
                                            </div>
                                        </button>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Route */}
                        <div className="grid md:grid-cols-2 gap-5">
                            <Field
                                label="Origin"
                                id="origin"
                                value={form.origin}
                                onChange={(v) => update("origin", v)}
                                placeholder="e.g. Gurugram, HR"
                                testid="quote-origin"
                            />
                            <Field
                                label="Destination"
                                id="destination"
                                value={form.destination}
                                onChange={(v) => update("destination", v)}
                                placeholder="e.g. Pune, MH"
                                testid="quote-destination"
                            />
                        </div>

                        <div className="grid md:grid-cols-2 gap-5">
                            <Field
                                label="Weight (kg)"
                                id="weight"
                                type="number"
                                value={form.weight}
                                onChange={(v) => update("weight", v)}
                                placeholder="e.g. 500"
                                testid="quote-weight"
                            />
                            <Field
                                label="Distance (km)"
                                id="distance"
                                type="number"
                                value={form.distance}
                                onChange={(v) => update("distance", v)}
                                placeholder="e.g. 1400"
                                testid="quote-distance"
                            />
                        </div>

                        {/* Urgency */}
                        <div>
                            <label className="block text-sm font-bold text-slate-900 mb-4">
                                Priority
                            </label>
                            <div className="grid sm:grid-cols-3 gap-3">
                                {urgencies.map((u) => {
                                    const active = form.urgency === u.key;
                                    return (
                                        <button
                                            type="button"
                                            key={u.key}
                                            data-testid={`urgency-${u.key}`}
                                            onClick={() => update("urgency", u.key)}
                                            className={`p-5 rounded-2xl border text-left transition-all ${
                                                active
                                                    ? "border-blue-600 bg-blue-50"
                                                    : "border-slate-200 hover:border-slate-400"
                                            }`}
                                        >
                                            <div className="font-display font-bold text-slate-900">
                                                {u.label}
                                            </div>
                                            <div className="text-xs text-slate-500 mt-0.5">{u.desc}</div>
                                        </button>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Contact */}
                        <div className="grid md:grid-cols-2 gap-5">
                            <Field
                                label="Your name (optional)"
                                id="name"
                                value={form.name}
                                onChange={(v) => update("name", v)}
                                placeholder="e.g. Rahul Mehta"
                                testid="quote-name"
                            />
                            <Field
                                label="Email (optional)"
                                id="email"
                                type="email"
                                value={form.email}
                                onChange={(v) => update("email", v)}
                                placeholder="rahul@company.com"
                                testid="quote-email"
                            />
                        </div>

                        {error && (
                            <div
                                data-testid="quote-error"
                                className="rounded-xl bg-red-50 border border-red-200 text-red-800 px-4 py-3 text-sm"
                            >
                                {error}
                            </div>
                        )}

                        <div className="flex flex-wrap items-center gap-4 pt-2">
                            <button
                                type="submit"
                                data-testid="quote-submit"
                                className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-slate-900 text-white font-bold hover:bg-blue-600 transition-colors"
                            >
                                Calculate quote <ArrowRight className="w-4 h-4" />
                            </button>
                            {quote && (
                                <button
                                    type="button"
                                    onClick={reset}
                                    data-testid="quote-reset"
                                    className="inline-flex items-center gap-2 px-6 py-4 rounded-full border border-slate-300 text-slate-700 font-semibold hover:bg-slate-50 transition-colors"
                                >
                                    <RotateCcw className="w-4 h-4" />
                                    Reset
                                </button>
                            )}
                        </div>
                    </form>
                </div>
            </section>

            {/* RESULT */}
            <AnimatePresence>
                {quote && (
                    <motion.section
                        id="quote-result"
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.5 }}
                        className="pb-24 sm:pb-32"
                        data-testid="quote-result"
                    >
                        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
                            <div className="rounded-3xl overflow-hidden border border-slate-200 bg-white shadow-xl">
                                <div className="grid md:grid-cols-5">
                                    <div className="md:col-span-3 p-8 md:p-12">
                                        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-green-50 border border-green-200 text-green-700 text-xs font-bold uppercase tracking-wider mb-5">
                                            <CheckCircle2 className="w-3.5 h-3.5" />
                                            Estimate ready
                                        </div>
                                        <h2 className="font-display text-3xl font-black text-slate-900 tracking-tighter">
                                            {form.origin || "Origin"}
                                            <span className="text-blue-600 mx-2">→</span>
                                            {form.destination || "Destination"}
                                        </h2>
                                        <div className="mt-2 text-slate-500">
                                            {form.weight} kg · {form.distance} km · {form.urgency}
                                        </div>

                                        <div className="mt-8 space-y-3">
                                            <Row label="Base + distance + weight" value={quote.subtotal} />
                                            <Row label="GST (18%)" value={quote.gst} />
                                            <div className="flex items-baseline justify-between pt-4 border-t border-slate-200">
                                                <div className="font-display text-lg font-bold text-slate-900">Total estimate</div>
                                                <div className="font-display text-4xl font-black text-slate-900 tracking-tighter">
                                                    <IndianRupee className="inline w-7 h-7 -mt-1" />
                                                    {quote.total.toLocaleString("en-IN")}
                                                </div>
                                            </div>
                                        </div>

                                        <div className="mt-6 text-xs text-slate-500 leading-relaxed">
                                            Indicative estimate based on typical lane economics. For a
                                            binding quote, please reach us at{" "}
                                            <a href="mailto:info@beetalogistics.com" className="font-semibold text-blue-600 hover:underline">
                                                info@beetalogistics.com
                                            </a>{" "}
                                            or{" "}
                                            <a href="tel:+919958018358" className="font-semibold text-blue-600 hover:underline">
                                                +91 99580 18358
                                            </a>
                                            .
                                        </div>
                                    </div>

                                    <div className="md:col-span-2 p-8 md:p-12 bg-slate-900 text-white">
                                        <div className="text-xs font-bold uppercase tracking-[0.2em] text-blue-400 mb-4">
                                            Delivery window
                                        </div>
                                        <div className="flex items-start gap-3">
                                            <Calendar className="w-6 h-6 text-blue-400 mt-1" />
                                            <div>
                                                <div className="font-display text-4xl font-black tracking-tighter">
                                                    {quote.etaDays}–{quote.etaDays + 1} days
                                                </div>
                                                <div className="text-slate-400 text-sm mt-1">
                                                    From pickup confirmation
                                                </div>
                                            </div>
                                        </div>

                                        <div className="mt-10 space-y-3 text-sm">
                                            {[
                                                { icon: Truck, t: "Live GPS tracking" },
                                                { icon: Package, t: "Insurance-eligible" },
                                                { icon: CheckCircle2, t: "ePOD within 24h" },
                                            ].map((b) => {
                                                const Icon = b.icon;
                                                return (
                                                    <div key={b.t} className="flex items-center gap-3 text-slate-300">
                                                        <Icon className="w-4 h-4 text-blue-400" /> {b.t}
                                                    </div>
                                                );
                                            })}
                                        </div>

                                        <button
                                            type="button"
                                            data-testid="quote-confirm"
                                            onClick={() =>
                                                alert(
                                                    "Thanks! For a binding quote, please email info@beetalogistics.com or call +91 99580 18358 with your estimate details.",
                                                )
                                            }
                                            className="mt-10 w-full inline-flex items-center justify-center gap-2 px-6 py-4 rounded-full bg-white text-slate-900 font-bold hover:bg-blue-600 hover:text-white transition-colors"
                                        >
                                            Confirm & get a binding quote
                                            <ArrowRight className="w-4 h-4" />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </motion.section>
                )}
            </AnimatePresence>
        </div>
    );
}

function Field({ label, id, value, onChange, placeholder, type = "text", testid }) {
    return (
        <div>
            <label htmlFor={id} className="block text-sm font-bold text-slate-900 mb-2">
                {label}
            </label>
            <input
                id={id}
                data-testid={testid}
                type={type}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                placeholder={placeholder}
                className="w-full px-4 py-3 rounded-xl border border-slate-300 bg-white text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-transparent"
            />
        </div>
    );
}

function Row({ label, value }) {
    return (
        <div className="flex items-baseline justify-between">
            <div className="text-slate-600">{label}</div>
            <div className="font-display font-bold text-slate-900">
                <IndianRupee className="inline w-3.5 h-3.5 -mt-0.5" />
                {value.toLocaleString("en-IN")}
            </div>
        </div>
    );
}
